import { Button, FoodsIcon, iconMapping } from "@foodpilot/foods";
import { Stack, useTheme, useMediaQuery } from "@mui/material";
import { Typography as FoodsTypography } from "@foodpilot/foods";
import { isAppInPDFMode } from "src/utils/rendering";

export type CallToActionProps = {
    label?: string;
    url?: string;
    width?: "auto" | "full" | "half" | "default";
    position?: "left" | "center" | "right";
    variant?: TypographyVariant;
    textColor?: string;
    icon?: keyof typeof iconMapping | "";
    iconPosition?: "left" | "right";
    target?: "_self" | "_blank";
};

export type TypographyVariant =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "caption"
    | "overline";

const CallToAction = (props: CallToActionProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const shouldHide =
        isAppInPDFMode() ||
        (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("from") === "templates");

    if (shouldHide) return null;
    const {
        label = "",
        url = "#",
        width = "default",
        position = "left",
        variant = "h2",
        icon = "",
        iconPosition = "left",
        textColor = "#ffffff",
        target = "_blank",
    } = props;
    const _url = url.startsWith("http") ? url : `https://${url}`;

    const widthMap = {
        default: "fit-content",
        auto: "auto",
        full: "100%",
        half: "50%",
    };

    const positionMap = {
        left: "flex-start",
        center: "center",
        right: "flex-end",
    };

    return (
        <Stack
            sx={{
                alignItems: positionMap[position],
            }}
        >
            <Button
                href={_url}
                target={target}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
                variant="primary"
                sx={{
                    width: widthMap[width],
                    color: textColor,
                    padding: isMobile ? theme.spacing(2) : theme.spacing(3),
                }}
            >
                <Stack
                    sx={{
                        gap: theme.spacing(1),
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {icon && iconPosition === "left" && <FoodsIcon size={2} icon={icon} />}
                    <FoodsTypography
                        sx={{
                            color: textColor,
                            fontWeight: variant === "body1" ? 600 : theme.typography.fontWeightBold,
                        }}
                        variant={variant}
                    >
                        {label}
                    </FoodsTypography>
                    {icon && iconPosition === "right" && <FoodsIcon size={2} icon={icon} />}
                </Stack>
            </Button>
        </Stack>
    );
};

export default CallToAction;
